import { useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux'
import { getFormattedDisplayName } from '../helper/getFormattedDisplayName'
import type { FieldMappingFormValue } from '../FieldMappingV2'
import { FieldsResponse, AppField } from '@/types/WorkflowCreate/fields'
import { FormLabel } from '@/components/ui/form'

type AutoFillProps = {
  setMappedFieldData: (data: FieldMappingFormValue[]) => void
  allFields: Record<string, FieldsResponse>
}

const createEmptyRow = (): FieldMappingFormValue => ({
  sourceField: '',
  destinationField: '',
  sourceFieldLabel: '',
  destinationFieldLabel: '',
  options: undefined,
  customSourceLabel: undefined,
  selectionIndexesRaw: ''
})

const extractLeafFieldsWithLabels = (fields: AppField[] = [], prefix: string) => {
  const result: Array<{ code: string, label: string }> = []

  const traverse = (nodes?: AppField[], breadcrumbs: string[] = []) => {
    nodes?.forEach(node => {
      const nextBreadcrumbs = [...breadcrumbs, node.displayName].filter(Boolean)
      if (node.fields?.length) {
        traverse(node.fields, nextBreadcrumbs)
      } else {
        const baseCode = node.codeChain || node.code
        if (!baseCode) return
        const codeChain = baseCode.includes('=>') ? baseCode : `${prefix}${baseCode}`
        result.push({
          code: codeChain,
          label: nextBreadcrumbs.join(' • ') || baseCode
        })
      }
    })
  }

  traverse(fields)
  return result
}

export const AutoFill = ({ setMappedFieldData, allFields }: AutoFillProps) => {
  const [isAutoFillEnabled, setIsAutoFillEnabled] = useState(false)
  const [selectedStep, setSelectedStep] = useState('')
  const { applications } = useSelector((state: RootState) => state.application)
  const { steps } = useSelector((state: RootState) => state.workflow.create)

  const fieldMappingStepOptions = Object.keys(allFields).map(stepKey => ({
    id: stepKey,
    value: stepKey,
    label: getFormattedDisplayName(stepKey, applications, steps)
  }))

  const resetSelection = () => {
    setMappedFieldData([createEmptyRow()])
    setSelectedStep('')
  }

  const handleAutoFillCheckboxChange = (checked: string | boolean) => {
    const enabled = checked === true
    setIsAutoFillEnabled(enabled)
    if (!enabled) resetSelection()
  }

  const handleStepSelectionChange = (value: string) => {
    if (!isAutoFillEnabled) return
    setSelectedStep(value)

    const branchKey = value
    const availableFields = allFields[branchKey]
    if (!availableFields) return setMappedFieldData([createEmptyRow()])

    const prefixedFields = extractLeafFieldsWithLabels(availableFields.responseFields, `${branchKey}=>`)

    if (!prefixedFields.length) {
      return setMappedFieldData([createEmptyRow()])
    }

    setMappedFieldData(
      prefixedFields.map(field => ({
        ...createEmptyRow(),
        sourceField: field.code,
        sourceFieldLabel: field.label
      }))
    )
  }

  return (
    <div className='flex items-start w-full mb-6'>
      <div className='flex items-center space-x-2 mr-4 mt-2 w-full'>
        <Checkbox id='autoFill' checked={isAutoFillEnabled} onCheckedChange={handleAutoFillCheckboxChange} />
        <FormLabel htmlFor='autoFill' className="font-semibold text-primary !text-sm !w-[275px] cursor-pointer">
          Autofill all available output fields from
        </FormLabel>
        <div className='text-primary bg-foreground w-[290px] ml-4'>
          <Select value={selectedStep} onValueChange={handleStepSelectionChange}>
            <SelectTrigger
              className='w-full h-10 bg-white text-primary rounded-md [&>span]:!line-clamp-none'
              disabled={!isAutoFillEnabled}
            >
              <SelectValue placeholder='Choose step' />
            </SelectTrigger>
            <SelectContent className='w-full bg-white text-primary'>
              {fieldMappingStepOptions.map(opt => (
                <SelectItem key={opt.id} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
